import { CountryCode } from '~/enums';
import React, { useContext, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { ValidationContext } from '~/components/hoc/withValidation';
import Card from '~/components/shared/Card/Card';
import CardHeader from '~/components/shared/CardHeader/CardHeader';
import CardSection from '~/components/shared/CardSection/CardSection';
import InputField from '~/components/shared/Input/InputField';
import { PhoneNumberPlaceholders } from '~/constants/markets';
import useTranslate from '~/hooks/useTranslate';
import * as offerActions from '~/store/actions/loanOfferActions';
import { RootState } from '~/store/types/sharedTypes';
import { Signer } from '~/types/types';

interface Props {
  signers: Array<Signer>;
}

const SignersCard: React.FC<Props> = (props) => {
  const translate = useTranslate();
  const dispatch = useDispatch();
  const validationContext = useContext(ValidationContext);
  const market = useSelector((state: RootState) => state.intl.market);
  const stateSigners = useSelector(
    (state: RootState) => state.loanOffer.userData.signers
  );

  /* Effect */
  useEffect(() => {
    dispatch(
      offerActions.updateOfferData({
        signers: props.signers,
      })
    );
  }, []);

  const signerEmailHandler = (
    e: React.ChangeEvent<HTMLInputElement>,
    signer: Signer
  ) => {
    validationContext.onChangeHOC(e);
    dispatch(
      offerActions.updateOfferSigners({
        email: e.currentTarget.value,
        signer_id: signer.signer_id,
      })
    );
  };

  const signerPhoneHandler = (
    e: React.ChangeEvent<HTMLInputElement>,
    signer: Signer
  ) => {
    validationContext.onChangeHOC(e);
    dispatch(
      offerActions.updateOfferSigners({
        phone: e.currentTarget.value,
        signer_id: signer.signer_id,
      })
    );
  };

  const getSignersTitle = () => {
    let translationKey = 'Title';
    if (
      market === CountryCode.NO &&
      stateSigners?.[0]?.type?.toLowerCase() === 'guarantor'
    ) {
      translationKey = 'GuarantorTitle';
    }
    return translate(`Signers.${translationKey}`) as string;
  };

  const getSignerNameLabel = (type: string, index: number) => {
    let translationKey = 'SignerName';
    if (market === CountryCode.NO && type?.toLowerCase() === 'guarantor') {
      translationKey = 'GuarantorName';
    }

    return `${translate(`Signers.${translationKey}`)} #${index + 1}`;
  };

  return (
    <>
      {!!stateSigners?.length && (
        <Card noPadding dataTestId="Signers-card">
          <CardHeader value={getSignersTitle()} />

          {stateSigners.map((signer: Signer, index: number) => (
            <CardSection
              key={signer.signer_id}
              title={getSignerNameLabel(signer?.type, index)}
              value={signer.name}
              borderTop
            >
              <InputField
                name={`email${index}`}
                value={signer.email || ''}
                onChange={(e) => signerEmailHandler(e, signer)}
                validationType="Email"
                placeholder={
                  translate('Signers.SignerEmailPlaceholder') as string
                }
              />
              <InputField
                name={`phone${index}`}
                onChange={(e) => signerPhoneHandler(e, signer)}
                value={signer.phone || ''}
                validationType="Phone"
                placeholder={
                  translate('Signers.SignerPhonePlaceholder', {
                    phoneNumberPlaceholder: PhoneNumberPlaceholders[market],
                  }) as string
                }
              />
            </CardSection>
          ))}
        </Card>
      )}
    </>
  );
};

export default SignersCard;
